import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { Product } from "../../types/product.types";
import {
  addProduct,
  getProducts,
  productDelete,
  productUpdate,
} from "../../services/productService";

interface ProductState {
  items: Product[];
  page: number;
  totalPages: number;
  nextPageCount: number;
  lastFetechedPage: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  search: string;
}

const initialState: ProductState = {
  items: [],
  page: 1,
  totalPages: 1,
  loading: true,
  nextPageCount: 10,
  lastFetechedPage: 0,
  error: null,
  hasMore: true,
  search: "",
};

export const fetchProducts = createAsyncThunk(
  "products/fetch",
  async (
    { page, search }: { page: number; search: string },
    { rejectWithValue }
  ) => {
    const { data, error } = (await getProducts(page, search)) as {
      data: ProductState;
      error: any;
    };

    if (error) return rejectWithValue(error);

    return {
      items: data.items,
      page: data.page,
      hasMore: data.hasMore,
      nextPageCount: data.nextPageCount,
    };
  }
);

export const updateProduct = createAsyncThunk(
  "products/updateProduct",
  async ({ product }: { product: Product }, { rejectWithValue }) => {
    const { data, error } = (await productUpdate(product)) as {
      data: Product | { message: string };
      error: { message: string };
    };

    if (error) {
      toast.error(error.message || "Failed to update product");
      return rejectWithValue(error);
    }

    return data;
  }
);

export const deleteProduct = createAsyncThunk(
  "delete/Product",
  async ({ productID }: { productID: string }, { rejectWithValue }) => {
    const { data, error } = (await productDelete(productID)) as {
      data: { message: string; id: string };
      error: { message: string };
    };

    if (error) return rejectWithValue(error);

    return data;
  }
);

export const createProduct = createAsyncThunk(
  "create/Product",
  async (
    { product }: { product: Omit<Product, "_id"> },
    { rejectWithValue }
  ) => {
    const { data, error } = (await addProduct(product)) as {
      data: Product;
      error: any;
    };

    if (error) {
      toast.error(error.message || "Failed to create product");
      return rejectWithValue(error);
    }

    return data;
  }
);

export const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    setSearch(state: ProductState, action: PayloadAction<string>) {
      state.search = action.payload as string;
      state.page = 1;
      state.hasMore = true;
      state.lastFetechedPage = 0;
    },
    resetProducts(state) {
      return initialState;
    },
    setLastFetchedPage(state: ProductState, action: PayloadAction<number>) {
      state.lastFetechedPage = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state: ProductState) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchProducts.fulfilled, (state: ProductState, action) => {
        const { items, page, hasMore, nextPageCount } =
          action.payload as ProductState;

        state.loading = false;
        state.page = page;
        state.hasMore = hasMore;
        state.nextPageCount = nextPageCount;

        if (page === 1) {
          state.items = items;
        } else {
          state.items.push(...items);
        }
      })

      .addCase(fetchProducts.rejected, (state: ProductState, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder.addCase(updateProduct.fulfilled, (state: ProductState, action) => {
      const updatedProduct = action.payload as Product;
      const index = state.items.findIndex(
        (product) => product._id === updatedProduct._id
      );
      if (index !== -1) {
        state.items[index] = updatedProduct;
      }
    });

    builder.addCase(deleteProduct.fulfilled, (state: ProductState, action) => {
      const deletedID = action.payload.id;
      state.items = state.items.filter((product) => product._id !== deletedID);
      toast.success("Product deleted successfully");
    });

    builder.addCase(createProduct.fulfilled, (state: ProductState, action) => {
      const newProduct = action.payload as Product;
      state.items.unshift(newProduct);
      toast.success("Product created successfully");
    });
  },
});

export const { setSearch, resetProducts, setPage, setLastFetchedPage } =
  productSlice.actions;
export default productSlice.reducer;
